import { useCallback, useEffect, useRef } from "react";

/**
 * Marks a horizontal scroller with data-overflow="start|end|both" so CSS can fade the
 * edge that actually has more content.
 *
 * The tab strips scroll sideways, and a native horizontal scrollbar sits as a thick
 * grey slab under the tabs on Windows. The fade is the affordance instead — it must be
 * driven from JS because a static gradient would wash out the first/last tab on the
 * strips that fit without scrolling.
 *
 * Returns a ref callback that wires itself up on attach and tears itself down on
 * detach. The observers live in the CALLBACK, not an effect keyed on a state-held node:
 * the strip mounts later than the hook (its host renders a collapsed placeholder until
 * the rows arrive), and holding the node in state would re-render that host — a whole
 * extra pass over a 69k-row statement table on Dubai — just to attach a listener.
 */
export function useScrollEdges() {
  const rafRef = useRef<number | null>(null);
  const detachRef = useRef<(() => void) | null>(null);

  const register = useCallback((el: HTMLDivElement | null) => {
    detachRef.current?.();
    detachRef.current = null;
    if (!el) return;

    const update = () => {
      rafRef.current = null;
      // 1px slack: fractional layout widths otherwise leave a permanent phantom edge.
      const start = el.scrollLeft > 1;
      const end = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
      const v = start && end ? "both" : start ? "start" : end ? "end" : "";
      if (v) el.dataset.overflow = v;
      else delete el.dataset.overflow;
    };
    const schedule = () => {
      if (rafRef.current === null) rafRef.current = requestAnimationFrame(update);
    };

    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null;
    // Children too: a search count appearing widens a tab without resizing the strip.
    const observeAll = () => {
      ro?.observe(el);
      for (const child of Array.from(el.children)) ro?.observe(child);
    };
    // A conditional tab (Auto-Matched, Cleared, Prior-Period) appearing changes how far
    // the strip scrolls without resizing anything already observed.
    const mo = typeof MutationObserver !== "undefined"
      ? new MutationObserver(() => { observeAll(); schedule(); })
      : null;
    mo?.observe(el, { childList: true });
    el.addEventListener("scroll", schedule, { passive: true });
    observeAll();
    schedule();

    detachRef.current = () => {
      el.removeEventListener("scroll", schedule);
      ro?.disconnect();
      mo?.disconnect();
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, []);

  // The ref callback only fires on attach/detach, so an unmount of the whole host has
  // to release the observers too.
  useEffect(() => () => { detachRef.current?.(); detachRef.current = null; }, []);

  return register;
}
